import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function SpeechTherapy() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/speech-therapy#webpage`,
    url: `${siteUrl}/services/speech-therapy`,
    name: 'Speech Therapy at Home Las Vegas | Celebrity Home Health',
    description: 'In-home speech-language therapy in Las Vegas — communication disorders, swallowing evaluation, stroke recovery, and cognitive-communication therapy. Medicare certified.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'SpeechPathology',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Speech Therapy', item: `${siteUrl}/services/speech-therapy` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Speech Therapy at Home Las Vegas | Celebrity Home Health';
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-speech-therapy" />
      <Header />
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/806893506bec2ef8e10f7048a270f583.jpg" alt="Speech Therapy" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6"><i className="ri-chat-voice-line"></i> Speech Therapy</div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Speech Therapy<br /><span className="text-amber-400">At Home</span> in Las Vegas</h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-8">Our licensed speech-language pathologists treat communication, language, voice, and swallowing disorders — delivered in the comfort of your Las Vegas home. Medicare certified.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Speech Therapy</a>
              <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i> 866-939-0330</a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">Our ST Services</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Speech Therapy Services</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Specialized treatment to restore communication and swallowing function after stroke, illness, or injury.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-chat-voice-line', title: 'Speech & Language', desc: 'Therapy for aphasia, articulation, fluency, and expressive/receptive language disorders.' },
              { icon: 'ri-restaurant-line', title: 'Swallowing Treatment', desc: 'Evaluation and therapy for dysphagia to ensure safe eating and drinking.' },
              { icon: 'ri-brain-line', title: 'Cognitive-Communication', desc: 'Treatment for memory, attention, problem-solving, and organizational deficits.' },
              { icon: 'ri-mic-line', title: 'Voice Therapy', desc: 'Treatment for vocal cord disorders, hoarseness, and voice quality impairments.' },
              { icon: 'ri-heart-pulse-line', title: 'Stroke Recovery', desc: 'Specialized programs to regain communication and swallowing abilities after stroke.' },
              { icon: 'ri-file-list-3-line', title: 'AAC Strategies', desc: 'Augmentative and alternative communication tools for patients with severe communication impairments.' },
            ].map((item, i) => (
              <div key={i} className="bg-stone-50 p-7 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4"><i className={`${item.icon} text-amber-700 text-lg`}></i></div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Find Your Voice Again</h2>
          <p className="text-slate-400 text-lg mb-8">Contact Celebrity Home Health to schedule a speech therapy evaluation at your home in Las Vegas, Henderson, or Summerlin.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Care Now</a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">View All Services</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
